import { useCallback, useEffect, useState } from "react";

import { api, getToken, onSessionEnded } from "../api/client";
import { handoffRefusal } from "../api/handoff";

/**
 * Auth state, held in React rather than read from localStorage during render —
 * writing the token to storage does not re-render anything on its own, so the
 * app would stay on the login screen after a successful sign-in.
 *
 * `refusal` is the other direction: the server closing the account under an
 * open session (expired, suspended, console blocked). The client has already
 * dropped the token by then; this is what puts the customer back on the login
 * screen with the reason instead of on a dashboard full of errors. It starts
 * as whatever the console handoff met, if this tab was opened by one and the
 * ticket was no longer worth anything (`api/handoff.ts`) — same screen, same
 * wording, one round trip earlier.
 */
export function useAuth() {
  const [token, setToken] = useState<string | null>(() => getToken());
  const [refusal, setRefusal] = useState<unknown>(() => handoffRefusal());

  const signedIn = useCallback(() => {
    setRefusal(null);
    setToken(getToken());
  }, []);
  const signOut = useCallback(() => {
    api.logout();
    setRefusal(null);
    setToken(null);
  }, []);

  useEffect(
    () =>
      onSessionEnded((error) => {
        setRefusal(error);
        setToken(null);
      }),
    [],
  );

  return { token, refusal, signedIn, signOut };
}
